import { useState } from "react";
import { useAgentSimScenarios } from "@hooks/useAgentSimScenarios";
import Button from "@components/ui/Button";
import Table, { TableHead, TableBody, TableRow, TableHeaderCell, TableCell } from "@components/ui/Table";
import { AGENT_PURPOSES } from "@constants/agentSim";
import ScenarioEditorDrawer from "./ScenarioEditorDrawer";
import RunNowConfirmModal from "./RunNowConfirmModal";

const selectClass = "px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none";

export default function ScenariosTab({ clinics, triggerRun }) {
  const h = useAgentSimScenarios();
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=create, obj=edit
  const [confirmRun, setConfirmRun] = useState(false);
  const clinicName = clinics.find((c) => c.id === h.clinicId)?.clinic_name || h.clinicId;
  const ready = h.clinicId && h.purpose;

  const onSave = async (payload) => {
    const ok = editing ? await h.update(editing.id, payload) : await h.create(payload);
    if (ok) setEditing(undefined);
  };

  return (
    <div className="space-y-4">
      {h.error && (
        <div className="flex justify-between bg-red-50 text-red-700 text-sm px-3 py-2 rounded">
          <span>{h.error}</span><button onClick={h.clearError}>✕</button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <select className={selectClass} aria-label="scenario-clinic" value={h.clinicId}
          onChange={(e) => h.setClinicId(e.target.value)}>
          <option value="">Select clinic…</option>
          {clinics.map((c) => <option key={c.id} value={c.id}>{c.clinic_name}</option>)}
        </select>
        <select className={selectClass} aria-label="scenario-purpose" value={h.purpose}
          onChange={(e) => h.setPurpose(e.target.value)}>
          <option value="">Select purpose…</option>
          {AGENT_PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <Button color="light" disabled={!ready || h.loading} onClick={() => h.refresh()}>
          {h.loading ? "Loading…" : "Reload"}
        </Button>
        <Button color="light" disabled={!ready} onClick={() => h.regenerate()}>Regenerate baselines</Button>
        <Button color="primary" disabled={!ready} onClick={() => setEditing(null)}>New scenario</Button>
        <Button color="green" disabled={!ready} onClick={() => setConfirmRun(true)}>Run now (live)</Button>
      </div>

      {!ready && <div className="text-sm text-gray-500">Select a clinic and purpose — scenarios load automatically.</div>}

      {ready && (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Origin</TableHeaderCell>
              <TableHeaderCell>Active</TableHeaderCell>
              <TableHeaderCell>Assertions</TableHeaderCell>
              <TableHeaderCell>Max turns</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {h.scenarios.map((s) => (
              <TableRow key={s.id}>
                <TableCell>{s.name}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded ${s.origin === "baseline" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>{s.origin}</span>
                </TableCell>
                <TableCell>{s.is_active ? "yes" : "no"}</TableCell>
                <TableCell>{(s.expected_outcome || []).length}</TableCell>
                <TableCell>{s.max_turns}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button color="light" size="xs" onClick={() => setEditing(s)}>Edit</Button>
                    {s.origin === "custom"
                      ? <Button color="light" size="xs" onClick={() => h.remove(s.id)}>Delete</Button>
                      : <Button color="light" size="xs" onClick={() => h.remove(s.id)}>Deactivate</Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {h.scenarios.length === 0 && (
              <TableRow><TableCell colSpan={6}>No scenarios — try "Regenerate baselines".</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <ScenarioEditorDrawer open={editing !== undefined} scenario={editing || null}
        onClose={() => setEditing(undefined)} onSave={onSave} />

      <RunNowConfirmModal show={confirmRun} clinicName={clinicName} purpose={h.purpose}
        onClose={() => setConfirmRun(false)}
        onConfirm={async () => { setConfirmRun(false); await triggerRun({ clinicId: h.clinicId, purpose: h.purpose }); }} />
    </div>
  );
}
